// components/dashboard/ExportToExcel.jsx
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Button, message, Tooltip } from "antd";
import { FaFileExport } from "react-icons/fa";

export default function ExportToExcel({ data, loading }) {
  const exportFile = async () => {
    if (!data) {
      message.warning("No hay datos para exportar");
      return;
    }
    
    const hasPersonas = data.personas && data.personas.length > 0;
    const hasLlamadas = data.llamadas && data.llamadas.length > 0;
    
    if (!hasPersonas && !hasLlamadas) {
      message.warning("No hay datos para exportar");
      return;
    }
    
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Sistema 724";
      workbook.created = new Date();
      
      const headerStyle = {
        font: { bold: true, color: { argb: "FFFFFFFF" }, size: 11 },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E5A8E" } },
        alignment: { horizontal: "center", vertical: "middle", wrapText: true },
        border: {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        },
      };

      const cellStyle = {
        alignment: { vertical: "middle", wrapText: true },
        border: {
          top: { style: "thin", color: { argb: "FFD3D3D3" } },
          left: { style: "thin", color: { argb: "FFD3D3D3" } },
          bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
          right: { style: "thin", color: { argb: "FFD3D3D3" } },
        },
      };

      // 1. HOJA: PERSONAS (sin cambios)
      if (hasPersonas) {
        const sheet = workbook.addWorksheet("Personas");
        sheet.columns = [
          { header: "Codigo Participante", key: "id_participante", width: 12 },
          { header: "Nombres", key: "nombres", width: 20 },
          { header: "Apellidos", key: "apellidos", width: 20 },
          { header: "Nombre Completo", key: "nombre_completo", width: 25 },
          { header: "Tipo Documento", key: "tipo_documento", width: 15 },
          { header: "Número Documento", key: "numero_documento", width: 18 },
          { header: "Género", key: "genero", width: 10 },
          { header: "Fecha Nacimiento", key: "fecha_nacimiento", width: 12 },
          { header: "Edad", key: "edad", width: 8 },
          { header: "Rango de Edad", key: "rango_edad", width: 12 },
          { header: "Correo Electrónico", key: "correo", width: 25 },
          { header: "Teléfono", key: "telefono", width: 15 },
          { header: "Código DANE Departamento", key: "cod_dane_departamento", width: 15 },
          { header: "Departamento", key: "departamento", width: 20 },
          { header: "Código DANE Municipio", key: "cod_dane_municipio", width: 15 },
          { header: "Municipio", key: "municipio", width: 20 },
          { header: "Ubicación Completa", key: "ubicacion_completa", width: 30 },
          { header: "Estado Participante", key: "estado_participante", width: 18 },
          { header: "Estado Validación", key: "estado_validacion", width: 18 },
          { header: "Total Llamadas", key: "total_llamadas", width: 12 },
          { header: "Primera Llamada", key: "primera_llamada", width: 20 },
          { header: "Segunda Llamada", key: "segunda_llamada", width: 20 },
          { header: "Tercera Llamada", key: "tercera_llamada", width: 20 },
        ];

        data.personas.forEach((p) => {
          sheet.addRow({
            id_participante: p.id_participante || '',
            nombres: p.nombres || '',
            apellidos: p.apellidos || '',
            nombre_completo: p.nombre_completo || `${p.nombres || ''} ${p.apellidos || ''}`.trim(),
            tipo_documento: p.tipo_documento_nombre || '',
            numero_documento: p.num_identificacion || '',
            genero: p.genero_nombre || '',
            fecha_nacimiento: p.fecha_nacimiento_formateada || '',
            edad: p.edad || '',
            rango_edad: p.rango_edad || '',
            correo: p.correo || '',
            telefono: p.telefono || '',
            cod_dane_departamento: p.codigo_dane_departamento || '',
            departamento: p.nombre_departamento || '',
            cod_dane_municipio: p.codigo_dane_municipio || '',
            municipio: p.nombre_municipio || '',
            ubicacion_completa: p.ubicacion_completa || '',
            estado_participante: p.estado_validacion_codigo === 'NO_CONTINUA' ? 'Inactivo' : 'Activo',
            estado_validacion: p.estado_validacion || '',
            total_llamadas: p.total_llamadas || 0,
            primera_llamada: p.primera_llamada,
            segunda_llamada: p.segunda_llamada,
            tercera_llamada: p.tercera_llamada,
          });
        });

        sheet.getRow(1).eachCell((cell) => Object.assign(cell, headerStyle));
        for (let i = 2; i <= sheet.rowCount; i++) {
          sheet.getRow(i).eachCell((cell) => Object.assign(cell, cellStyle));
        }
        sheet.views = [{ state: 'frozen', ySplit: 1 }];
      }

      // 2. HOJA: LLAMADAS (CON NUEVOS CAMPOS AGREGADOS)
      if (hasLlamadas) {
        const sheet = workbook.addWorksheet("Llamadas");
        sheet.columns = [
          // ==== IDENTIFICACIÓN ====
          { header: "Codigo Participante", key: "id_participante", width: 12 },
          { header: "Codigo Llamada", key: "id_llamada", width: 12 },
          { header: "N° Intento", key: "numero_intento", width: 10 },
          { header: "Secuencia Llamada", key: "secuencia_llamada", width: 12 },
          
          // ==== DATOS DEL PARTICIPANTE ====
          { header: "Nombre Completo", key: "nombre_participante", width: 25 },
          { header: "Documento", key: "documento_participante", width: 18 },
          { header: "Teléfono", key: "telefono_participante", width: 15 },
          
          // ==== UBICACIÓN ====
          { header: "Código DANE Depto", key: "cod_dane_departamento", width: 15 },
          { header: "Departamento", key: "departamento_participante", width: 20 },
          { header: "Código DANE Municipio", key: "cod_dane_municipio", width: 15 },
          { header: "Municipio", key: "municipio_participante", width: 20 },
          
          // ==== ESTADO DE LLAMADA ====
          { header: "Estado Llamada", key: "estado_llamada", width: 20 },
          { header: "Descripción Estado", key: "descripcion_estado", width: 25 },
          { header: "Subestado Llamada", key: "subestado_llamada", width: 20 },
          { header: "Descripción Subestado", key: "descripcion_subestado", width: 25 },
          
          // ==== ELEGIBILIDAD ====
          { header: "¿Es Elegible?", key: "elegibilidad_texto", width: 15 },
          { header: "¿Tuvo Contacto?", key: "tuvo_contacto", width: 12 },
          { header: "¿Requiere Seguimiento?", key: "requiere_seguimiento", width: 15 },
          
          // ==== MOTIVO PRINCIPAL ====
          { header: "Motivo No Elegible", key: "motivo_no_elegible", width: 25 },
          { header: "Descripción Motivo", key: "descripcion_motivo", width: 30 },
          
          // ==== MOTIVOS ESPECÍFICOS - NUEVOS CAMPOS ====
          { header: "¿Información no comprobable?", key: "motivo_info_comprobable", width: 22 },
          { header: "¿Tres intentos sin respuesta?", key: "motivo_tres_intentos", width: 22 },
          { header: "¿Otro motivo?", key: "motivo_otro_cual", width: 15 },
          { header: "Detalle otro motivo", key: "motivo_otro_detalle", width: 25 },
          
          // ==== OBSERVACIONES ====
          { header: "Observación No Elegible", key: "observacion_no_elegible", width: 25 },
          { header: "Observación General", key: "observacion_general", width: 30 },
          
          // ==== UBICACIÓN FUERA TERRITORIO ====
          { header: "Depto fuera territorio", key: "departamento_fuera_territorio", width: 22 },
          { header: "Municipio fuera territorio", key: "municipio_fuera_territorio", width: 22 },
          
          // ==== INFORMACIÓN DE REGISTRO ====
          { header: "Responsable", key: "usuario", width: 20 },
          { header: "IP", key: "ip", width: 15 },
          { header: "Duración", key: "duracion_formateada", width: 15 },
          
          // ==== FECHAS ====
          { header: "Fecha Contacto", key: "fecha_hora_completa", width: 20 },
          { header: "Fecha Registro", key: "fecha_registro", width: 18 },
        ];

        data.llamadas.forEach((l) => {
          sheet.addRow({
            // IDENTIFICACIÓN
            id_participante: l.participante_id || '',
            id_llamada: l.id_llamada || '',
            numero_intento: l.numero_intento || '',
            secuencia_llamada: l.secuencia_llamada_participante || '',
            
            // DATOS PARTICIPANTE
            nombre_participante: l.nombre_participante || '',
            documento_participante: l.identificacion_participante || '',
            telefono_participante: l.telefono_participante || '',
            
            // UBICACIÓN
            cod_dane_departamento: l.codigo_dane_departamento || '',
            departamento_participante: l.departamento_participante || '',
            cod_dane_municipio: l.codigo_dane_municipio || '',
            municipio_participante: l.municipio_participante || '',
            
            // ESTADO LLAMADA
            estado_llamada: l.estado_llamada_nombre || '',
            descripcion_estado: l.estado_llamada_descripcion || '',
            subestado_llamada: l.subestado_llamada_nombre || '',
            descripcion_subestado: l.subestado_llamada_descripcion || '',
            
            // ELEGIBILIDAD
            elegibilidad_texto: l.elegibilidad_texto || '',
            tuvo_contacto: l.tuvo_contacto || 'No',
            requiere_seguimiento: l.requiere_seguimiento || 'No',
            
            // MOTIVO PRINCIPAL
            motivo_no_elegible: l.motivo_no_elegible_nombre || '',
            descripcion_motivo: l.motivo_no_elegible_descripcion || '',
            
            // MOTIVOS ESPECÍFICOS (NUEVOS)
            motivo_info_comprobable: l.motivo_info_comprobable || 'No aplica',
            motivo_tres_intentos: l.motivo_tres_intentos || 'No aplica',
            motivo_otro_cual: l.motivo_otro_cual || 'No aplica',
            resumen_motivos_especificos: l.resumen_motivos_especificos || 'No aplica',
            
            // OBSERVACIONES
            observacion_no_elegible: l.observacion_no_elegible || '',
            observacion_general: l.observacion_general || '',
            
            // UBICACIÓN FUERA TERRITORIO
            departamento_fuera_territorio: l.departamento_fuera_territorio || '',
            municipio_fuera_territorio: l.municipio_fuera_territorio || '',
            
            // INFORMACIÓN DE REGISTRO
            usuario: l.usuario_llamada || '',
            ip: l.ip_usuario || '',
            duracion_formateada: l.duracion_formateada || 'No registrada',
            
            // FECHAS
            fecha_hora_completa: l.fecha_hora_completa || '',
            fecha_registro: l.fecha_registro_formateada || '',
          });
        });

        sheet.getRow(1).eachCell((cell) => Object.assign(cell, headerStyle));
        for (let i = 2; i <= sheet.rowCount; i++) {
          sheet.getRow(i).eachCell((cell) => Object.assign(cell, cellStyle));
        }
        sheet.views = [{ state: 'frozen', ySplit: 1 }];
        
        // Aplicar formato condicional para mejor visualización
        sheet.eachRow((row, rowNumber) => {
          if (rowNumber > 1) {
            // Resaltar filas donde no es elegible
            const esElegible = row.getCell('O').value; // Columna O: "¿Es Elegible?"
            if (esElegible === 'No') {
              row.eachCell((cell) => {
                cell.fill = {
                  type: 'pattern',
                  pattern: 'solid',
                  fgColor: { argb: 'FFFFF2F0' } // Rojo claro
                };
              });
            }
          }
        });
      }

      // 3. HOJA: RESUMEN (actualizar con estadísticas de motivos)
      if (data.resumen) {
        const sheet = workbook.addWorksheet("Resumen");
        
        // Calcular estadísticas de motivos específicos
        let totalConMotivosEspecificos = 0;
        let totalInfoNoComprobable = 0;
        let totalTresIntentos = 0;
        let totalOtroMotivo = 0;
        
        if (hasLlamadas) {
          totalConMotivosEspecificos = data.llamadas.filter(l => l.tiene_motivos_especificos === 'Sí').length;
          totalInfoNoComprobable = data.llamadas.filter(l => l.motivo_info_comprobable === 'Sí').length;
          totalTresIntentos = data.llamadas.filter(l => l.motivo_tres_intentos === 'Sí').length;
          totalOtroMotivo = data.llamadas.filter(l => l.motivo_otro_cual === 'Sí').length;
        }
        
        const resumenData = [
          // SECCIÓN: GENERAL
          ["TOTAL PARTICIPANTES", data.resumen.total_participantes || 0],
          ["TOTAL MUNICIPIOS", data.resumen.total_municipios || 0],
          ["TOTAL DEPARTAMENTOS", data.resumen.total_departamentos || 0],
          ["", ""],
          
          // SECCIÓN: GÉNERO
          ["TOTAL MASCULINO", data.resumen.total_masculino || 0],
          ["TOTAL FEMENINO", data.resumen.total_femenino || 0],
          ["TOTAL OTRO GÉNERO", data.resumen.total_otro_genero || 0],
          ["", ""],
          
          // SECCIÓN: ESTADOS
          ["VERIFICADOS", data.resumen.total_verificados || 0],
          ["EN PROCESO", data.resumen.total_en_proceso || 0],
          ["NO CONTINÚA", data.resumen.total_no_continua || 0],
          ["NO VERIFICADOS", data.resumen.total_no_verificados || 0],
          ["SIN VALIDACIÓN", data.resumen.total_sin_validacion || 0],
          ["", ""],
          
          // SECCIÓN: PORCENTAJES
          ["% VERIFICADOS", data.resumen.porcentaje_verificados ? `${data.resumen.porcentaje_verificados}%` : "0%"],
          ["", ""],
          
          // SECCIÓN: CONTACTO
          ["CON TELÉFONO", data.resumen.total_con_telefono || 0],
          ["CON CORREO", data.resumen.total_con_correo || 0],
          ["SIN CONTACTO", data.resumen.total_sin_contacto || 0],
          ["", ""],
          
          // SECCIÓN: LLAMADAS
          ["TOTAL LLAMADAS REALIZADAS", data.resumen.total_llamadas_realizadas || 0],
          ["PROMEDIO LLAMADAS POR PARTICIPANTE", data.resumen.promedio_llamadas_por_participante || 0],
          ["", ""],
          
          // SECCIÓN: MOTIVOS ESPECÍFICOS (NUEVA)
          ["LLAMADAS CON MOTIVOS ESPECÍFICOS", totalConMotivosEspecificos],
          ["  • Información no comprobable", totalInfoNoComprobable],
          ["  • Tres intentos sin respuesta", totalTresIntentos],
          ["  • Otro motivo", totalOtroMotivo],
          ["", ""],
          
          // SECCIÓN: EDAD
          ["EDAD PROMEDIO", data.resumen.edad_promedio ? `${Math.round(data.resumen.edad_promedio)} años` : "N/A"],
          ["EDAD MÍNIMA", data.resumen.edad_minima ? `${data.resumen.edad_minima} años` : "N/A"],
          ["EDAD MÁXIMA", data.resumen.edad_maxima ? `${data.resumen.edad_maxima} años` : "N/A"],
          ["", ""],
          
          // SECCIÓN: FECHAS
          ["PRIMER REGISTRO", data.resumen.fecha_primer_registro ?
            new Date(data.resumen.fecha_primer_registro).toLocaleString('es-ES') : 'N/A'],
          ["ÚLTIMO REGISTRO", data.resumen.fecha_ultimo_registro ?
            new Date(data.resumen.fecha_ultimo_registro).toLocaleString('es-ES') : 'N/A'],
          ["DÍAS DE RANGO", data.resumen.dias_rango_registros || 0],
          ["", ""],
          
          // SECCIÓN: METADATA
          ["FECHA DE GENERACIÓN", data.metadata?.fecha_generacion ?
            new Date(data.metadata.fecha_generacion).toLocaleString('es-ES') : new Date().toLocaleString('es-ES')],
          ["TOTAL REGISTROS EXPORTADOS", data.metadata?.total_registros || 0],
        ];

        sheet.columns = [
          { header: "Métrica", key: "metrica", width: 40 },
          { header: "Valor", key: "valor", width: 25 },
        ];

        resumenData.forEach((row) => {
          sheet.addRow({ metrica: row[0], valor: row[1] });
        });

        sheet.getRow(1).eachCell((cell) => Object.assign(cell, headerStyle));
        
        for (let i = 2; i <= sheet.rowCount; i++) {
          sheet.getRow(i).eachCell((cell) => Object.assign(cell, cellStyle));
          
          // Formato especial para secciones de motivos
          const cellValue = sheet.getCell(`A${i}`).value;
          if (cellValue && cellValue.includes("•")) {
            sheet.getCell(`A${i}`).font = { bold: false, size: 11 };
            sheet.getCell(`A${i}`).alignment = { horizontal: 'left', indent: 2 };
          }
        }

        // Ajustar alturas de filas
        sheet.eachRow((row, rowNumber) => {
          if (rowNumber > 1 && row.getCell(1).value === "") {
            row.height = 10;
          }
        });
      }

      // 4. HOJA: FILTROS (sin cambios)
      if (data.metadata?.filtros_aplicados) {
        const sheet = workbook.addWorksheet("Filtros");
        const filtrosData = [
          ["FILTROS APLICADOS EN LA EXPORTACIÓN", ""],
          ["", ""],
          ["Fecha de Exportación", new Date().toLocaleString('es-ES')],
          ["", ""],
        ];

        if (data.metadata.filtros_aplicados) {
          Object.entries(data.metadata.filtros_aplicados).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              filtrosData.push([key.toString().toUpperCase().replace(/_/g, ' '), value]);
            }
          });
        }

        sheet.columns = [
          { header: "Parámetro", key: "parametro", width: 30 },
          { header: "Valor", key: "valor", width: 30 },
        ];

        filtrosData.forEach((row) => {
          sheet.addRow({ parametro: row[0], valor: row[1] });
        });

        sheet.getRow(1).eachCell((cell) => Object.assign(cell, headerStyle));
        for (let i = 2; i <= sheet.rowCount; i++) {
          sheet.getRow(i).eachCell((cell) => Object.assign(cell, cellStyle));
        }
      }

      // Generar y descargar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const fechaActual = new Date();
      const fechaFormato = `${fechaActual.getFullYear()}${String(fechaActual.getMonth() + 1).padStart(2, '0')}${String(fechaActual.getDate()).padStart(2, '0')}_${String(fechaActual.getHours()).padStart(2, '0')}${String(fechaActual.getMinutes()).padStart(2, '0')}`;
      const fileName = `exportacion_sistema_724_${fechaFormato}.xlsx`;
      
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        fileName
      );
      
      message.success("Archivo exportado exitosamente");
      
    } catch (error) {
      console.error("Error al exportar:", error);
      message.error("Error al generar el archivo Excel");
    }
  };

  return (
    <Tooltip title="Exportar datos a Excel">
      <Button
        type="primary"
        onClick={exportFile}
        disabled={!data || loading}
        loading={loading}
        icon={<FaFileExport />}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#2E5A8E',
          borderColor: '#2E5A8E'
        }}
      >
        Exportar a Excel
      </Button>
    </Tooltip>
  );
}